import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Avatar,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  getUsuarios,
  deleteUsuario,
  updateUsuario,
} from "../services/usuarioService";

export default function UserList() {
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUsuario, setEditingUsuario] = useState(null);

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      const usuariosData = await getUsuarios();
      setUsuarios(usuariosData);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
    }
  };

  const handleDelete = async (usuarioId) => {
    try {
      await deleteUsuario(usuarioId);
      setUsuarios(usuarios.filter((usuario) => usuario.id !== usuarioId));
      alert("Usuario eliminado exitosamente");
    } catch (error) {
      console.error("Error al eliminar el usuario:", error);
      alert("Ocurrió un error al eliminar el usuario");
    }
  };

  const handleSaveUsuario = async () => {
    try {
      const updatedFields = {};
      if (editingUsuario.nombreCompleto) {
        updatedFields.nombreCompleto = editingUsuario.nombreCompleto;
      }
      if (editingUsuario.sede) {
        updatedFields.sede = editingUsuario.sede;
      }
      if (editingUsuario.especialidad) {
        updatedFields.especialidad = editingUsuario.especialidad;
      }
      if (editingUsuario.nuevaFoto) {
        updatedFields.nuevaFoto = editingUsuario.nuevaFoto;
      }
      if (editingUsuario.professionalExperience) {
        updatedFields.professionalExperience =
          editingUsuario.professionalExperience;
      }
      await updateUsuario(editingUsuario.id, updatedFields);
      fetchUsuarios();
      alert("Usuario actualizado exitosamente");
      setEditingUsuario(null);
    } catch (error) {
      console.error("Error al actualizar el usuario:", error);
      alert("Ocurrió un error al actualizar el usuario");
    }
  };

  const filteredUsuarios = usuarios.filter((usuario) => {
    const nombreCompleto =
      typeof usuario.nombreCompleto === "string"
        ? usuario.nombreCompleto
        : "";
    return nombreCompleto.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom>
        Directorio Médico web | Encuentra a los Mejores Especialistas
      </Typography>

      {/* Campo de búsqueda */}
      <TextField
        label="Buscar usuario"
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 2 }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <TableContainer component={Paper} sx={{ marginTop: 10, maxHeight: 700 }}>
        <Table stickyHeader aria-label="tabla de usuarios">
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Foto</TableCell>
              <TableCell>Nombre Completo</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Sede</TableCell>
              <TableCell>Especialidad</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsuarios.length > 0 ? (
              filteredUsuarios.map((usuario, index) => (
                <TableRow key={usuario.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Avatar
                      src={usuario.profileImage || usuario.fotoPerfil}
                      alt={usuario.nombreCompleto}
                    />
                  </TableCell>
                  <TableCell>{usuario.nombreCompleto}</TableCell>
                  <TableCell>{usuario.email}</TableCell>
                  <TableCell>{usuario.sede}</TableCell>
                  <TableCell>{usuario.especialidad}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        setEditingUsuario({
                          ...usuario,
                          professionalExperience: usuario.professionalExperience
                            ? [...usuario.professionalExperience]
                            : [],
                        })
                      }
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleDelete(usuario.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No se encontraron usuarios
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de edición */}
      {editingUsuario && (
        <Dialog
          open={Boolean(editingUsuario)}
          onClose={() => setEditingUsuario(null)}
        >
          <DialogTitle>Editar Usuario</DialogTitle>
          <DialogContent>
            {/* Campo para editar el nombre */}
            <TextField
              label="Nombre Completo"
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
              value={editingUsuario.nombreCompleto || ""}
              onChange={(e) =>
                setEditingUsuario({
                  ...editingUsuario,
                  nombreCompleto: e.target.value,
                })
              }
            />

            {/* Campo para seleccionar la sede */}
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Sede</InputLabel>
              <Select
                value={editingUsuario.sede || ""}
                label="Sede"
                onChange={(e) =>
                  setEditingUsuario({ ...editingUsuario, sede: e.target.value })
                }
              >
                <MenuItem value="Clínica de la Costa - Barranquilla">
                  Clínica de la Costa - Barranquilla
                </MenuItem>
                <MenuItem value="Clínica de la Costa - Riohacha">
                  Clínica de la Costa - Riohacha
                </MenuItem>
                <MenuItem value="Clínica de la Costa - Cartagena">
                  Clínica de la Costa - Cartagena
                </MenuItem>
                <MenuItem value="Clínica de la Costa - Santa Marta">
                  Clínica de la Costa - Santa Marta
                </MenuItem>
              </Select>
            </FormControl>

            {/* Campo para ingresar la especialidad */}
            <TextField
              label="Especialidad"
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
              value={editingUsuario.especialidad || ""}
              onChange={(e) =>
                setEditingUsuario({
                  ...editingUsuario,
                  especialidad: e.target.value,
                })
              }
            />

            {/* Campo para subir una nueva foto */}
            <Button
              variant="contained"
              component="label"
              sx={{ marginBottom: 2 }}
            >
              Subir Nueva Foto
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setEditingUsuario({ ...editingUsuario, nuevaFoto: file });
                  }
                }}
              />
            </Button>

            {/* Campos para la Experiencia Profesional */}
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Experiencia Profesional
            </Typography>
            {editingUsuario.professionalExperience &&
              editingUsuario.professionalExperience.map((exp, index) => (
                <div key={index} style={{ marginBottom: "16px" }}>
                  <TextField
                    label="Institución"
                    variant="outlined"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={exp.institucionTrabajo || ""}
                    onChange={(e) => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp[index].institucionTrabajo = e.target.value;
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  />
                  <TextField
                    label="Posición"
                    variant="outlined"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={exp.posicion || ""}
                    onChange={(e) => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp[index].posicion = e.target.value;
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  />
                  <TextField
                    label="Descripción"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ marginBottom: 2 }}
                    value={exp.descripcionExperiencia || ""}
                    onChange={(e) => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp[index].descripcionExperiencia = e.target.value;
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  />
                  <TextField
                    label="Fecha Inicio"
                    variant="outlined"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={exp.fechaInicio || ""}
                    onChange={(e) => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp[index].fechaInicio = e.target.value;
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  />
                  <TextField
                    label="Fecha Fin"
                    variant="outlined"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={exp.fechaFin || ""}
                    onChange={(e) => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp[index].fechaFin = e.target.value;
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  />
                  <IconButton
                    color="secondary"
                    onClick={() => {
                      const updatedExp = [
                        ...editingUsuario.professionalExperience,
                      ];
                      updatedExp.splice(index, 1);
                      setEditingUsuario({
                        ...editingUsuario,
                        professionalExperience: updatedExp,
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
            <Button
              variant="contained"
              onClick={() => {
                const newExp = {
                  institucionTrabajo: "",
                  posicion: "",
                  descripcionExperiencia: "",
                  fechaInicio: "",
                  fechaFin: "",
                };
                setEditingUsuario({
                  ...editingUsuario,
                  professionalExperience: [
                    ...(editingUsuario.professionalExperience || []),
                    newExp,
                  ],
                });
              }}
            >
              Agregar Experiencia
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingUsuario(null)}>Cancelar</Button>
            <Button onClick={handleSaveUsuario}>Guardar</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}